import { useMoviesStore } from "@multicines/stores";
import { getApiMessageError } from "@multicines/utils";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/utc";
import LoadingSVG from "images/loading.svg";
import React, { useMemo, useState } from "react";

import { getBorderColor, getTextColor } from "./ScheduleSession.helpers";
import { getBackgroundColor } from "./ScheduleSession.helpers";
import Styles from "./ScheduleSession.styles";
import { ScheduleSessionProps as Props } from "./ScheduleSession.types";
import { notify } from "config/bugsnag.config";
import useAnalytics from "hooks/analytics/useAnalytics";
import usePendingOrder from "hooks/usePendingOrder";
import useTalkShop from "hooks/useTalkShop";
import { dismissErrorNotification } from "utils/notifications.utils";
import { createErrorNotification } from "utils/notifications.utils";

dayjs.extend(customParseFormat);

const ScheduleSession: React.FC<Props> = props => {
  const { session, className, disabled, onClick } = props;
  const { isTalkShop, navigateWithTalkShop } = useTalkShop();
  const analyticsHook = useAnalytics({
    isTalkShop
  });
  const { seatsAvailable, showtime } = session ?? {};
  const setSelectedSession = useMoviesStore(store => store.setSelectedSession);
  const selectedMovie = useMoviesStore(store => store.selectedMovie);
  const depleted = seatsAvailable === 0;
  const { validatePendingOrder } = usePendingOrder();
  const [isLoading, setIsLoading] = useState(false);
  const unavailable = useMemo(
    () => dayjs().subtract(20, "minutes").isAfter(dayjs(showtime), "minutes"),
    [showtime]
  );
  const text = useMemo(() => dayjs(showtime).format("HH:mm"), [showtime]);
  const customTextStyle = { color: getTextColor(unavailable, depleted) };
  const customViewStyle = {
    borderColor: getBorderColor(unavailable, depleted),
    backgroundColor: getBackgroundColor(unavailable, depleted)
  };

  const { logSelectHour } = analyticsHook;

  const { title } = selectedMovie ?? {};

  const navigateToTickets = () => {
    setSelectedSession(session);
    logSelectHour({
      hour: session.showtime,
      movie_name: title
    });
    navigateWithTalkShop("/tickets");
  };

  const clickHandler = async () => {
    setIsLoading(true);

    const onContinue = () => {
      onClick?.();
      navigateToTickets();
      setIsLoading(false);
    };

    const onError = (error: string) => {
      createErrorNotification(
        error,
        undefined,
        dismissErrorNotification,
        1000 * 30
      );
      setIsLoading(false);
    };

    try {
      await validatePendingOrder(onContinue, onError);
    } catch (e) {
      notify(e, "clickHandler - ScheduleSession");
      setIsLoading(false);
      const message = getApiMessageError(e);
      createErrorNotification(message);
    }
  };

  return (
    <Styles
      className={`ScheduleSession ${className}`}
      unavailable={unavailable}
      depleted={depleted}
      disabled={unavailable || depleted || isLoading || disabled}
      backgroundColor={customViewStyle.backgroundColor}
      borderColor={customViewStyle.borderColor}
      customTextStyle={customTextStyle.color}
      onClick={clickHandler}
    >
      {isLoading ? (
        <div className="ScheduleSession__loading">
          <LoadingSVG viewBox="0 0 66 66" />
        </div>
      ) : (
        <p className="ScheduleSession__text">{text}</p>
      )}
    </Styles>
  );
};

ScheduleSession.defaultProps = {
  className: ""
};

export default ScheduleSession;
